import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageFade from "../../../components/Login/PageFade";
import MessageModal from "../../../components/AdminComponents/Modals/MessageModal";
import closeButton from "../../../assets/images/close.png";
import { getEmployeesForAttendance, saveBulkAttendanceAction } from "../../../services/AppService";
import { getAllDepartments } from "../../../services/AdminServices";
import ConfirmActionModal from "../../../components/AdminComponents/Modals/ConfirmActionModal";

const MarkAttendance = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
    const [deptId, setDeptId] = useState("All");
    const [departments, setDepartments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [originalEmployees, setOriginalEmployees] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false); // Track if 'Show' was clicked

    const [modifiedIds, setModifiedIds] = useState(new Set());

    const [messageModal, setMessageModal] = useState({ open: false, type: "success", title: "", message: "" });
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const today = new Date().toISOString().split('T')[0];


    const ATTENDANCE_STATUSES = [
        { id: 1, name: "Present" },
        { id: 2, name: "On Duty" },
        { id: 3, name: "Leave" },
        { id: 4, name: "Absent" },
        { id: 5, name: "Late Mark" }
    ];

    useEffect(() => {
        loadDepartments();
    }, []);

    const loadDepartments = async () => {
        try {
            const data = await getAllDepartments(); //
            setDepartments(data.data || []);
        } catch (error) {
            console.error("Failed to load departments");
        }
    };

    // Clicking "Show" fetches the list
    const handleShow = async () => {
        try {
            setLoading(true);
            const data = await getEmployeesForAttendance(deptId, date);
            const formattedData = data || [];
            setEmployees(data || []);
            setModifiedIds(new Set());
            setHasSearched(true);
            // setFiltersChanged(false);
        } catch (error) {
            setMessageModal({ open: true, type: "error", title: "Error", message: "Failed to fetch employee list" });
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = (empId, statusId) => {
        // Update the data
        setEmployees(prev => prev.map(emp =>
            emp.employeeId === empId ? { ...emp, statusId: parseInt(statusId) } : emp
        ));

        // Track that this specific ID was touched
        setModifiedIds(prev => new Set(prev).add(empId));
        // setFiltersChanged(false); 
    };

    const isDataModified = () => {
        return JSON.stringify(employees) !== JSON.stringify(originalEmployees);
    };

    const initiateSave = () => {
        // Validation 1: Check if any value is selected
        const markedCount = employees.filter(emp => emp.statusId !== null && emp.statusId !== "").length;
        if (markedCount === 0) {
            setMessageModal({ open: true, type: "error", title: "Validation", message: "Please mark attendance." });
            return;
        }

        // Validation 2: Professional check - only allow save if data is new/modified
        if (!isDataModified()) {
            setMessageModal({ open: true, type: "error", title: "No Changes", message: "No new attendance values selected to save." });
            return;
        }

        setShowConfirmModal(true);
    };

    const handleSaveFinal = async () => {
        setShowConfirmModal(false);
        const payload = {
            attendanceDate: date,
            records: employees
                .filter(emp => modifiedIds.has(emp.employeeId))
                .map(emp => ({
                    employeeId: emp.employeeId,
                    departmentId: emp.departmentId,
                    designationId: emp.designationId,
                    statusId: emp.statusId
                }))
        };

        try {
            setLoading(true);
            const res = await saveBulkAttendanceAction(payload);
            const { newlyCreated, updated } = res.data;
            setMessageModal({
                open: true,
                type: "success",
                title: "Attendance Saved",
                message: `Processed ${newlyCreated + updated} records. (New: ${newlyCreated}, Updated: ${updated})`
            });
            handleShow();
        } catch (error) {
            setMessageModal({ open: true, type: "error", title: "Error", message: error.response?.data?.message || "Failed to save attendance" });
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageFade>
            <div className="bg-white shadow-lg rounded-2xl p-6 border border-gray-100">
                <div className="flex justify-between items-center border-b pb-3 mb-6">
                    <h4 className="text-xl font-bold text-[#2c5282] tracking-tight">{t('attendance.markAttendance')}</h4>
                    <img src={closeButton} className="w-6 h-6 cursor-pointer hover:opacity-70 transition-opacity" alt="Close" onClick={() => navigate(-1)} />
                </div>

                {/* Selection Controls */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end mb-8 bg-slate-50 p-5 rounded-xl border border-slate-200">
                    <div className="md:col-span-4 flex flex-col gap-1">
                        <label className="text-sm font-bold text-gray-500 uppercase ml-1">{t('attendance.markAttendance')} <span className="text-red-500">*</span></label>
                        <input type="date" max={today} value={date} onChange={(e) => { setDate(e.target.value); setHasSearched(false); }} className="border-2 border-slate-200 p-2 rounded-lg focus:border-blue-400 outline-none transition-all bg-white font-semibold text-gray-700" />
                    </div>
                    <div className="md:col-span-6 flex flex-col gap-1">
                        <label className="text-sm font-bold text-gray-500 uppercase ml-1">{t('attendance.department')} <span className="text-red-500">*</span></label>
                        <select value={deptId} onChange={(e) => { setDeptId(e.target.value); setHasSearched(false); }} className="border-2 border-slate-200 p-2 rounded-lg focus:border-blue-400 outline-none transition-all bg-white font-semibold text-gray-700">
                            <option value="All">All Departments</option>
                            {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                        </select>
                    </div>
                    <div className="md:col-span-2">
                        <button onClick={handleShow} disabled={loading} className="text-sm bg-[#3182ce] text-white py-2 px-4 rounded-lg font-bold hover:bg-[#2c5282] shadow-md transition-all active:scale-95 disabled:opacity-50">
                            {loading ? "Loading..." : <span>{t('common.show')}</span>}
                        </button>
                    </div>

                </div>

                {/* Attendance Sheet */}
                {employees.length > 0 ? (
                    <div className="overflow-hidden border border-slate-200 rounded-lg shadow-sm">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-[#a0adf1] text-white uppercase text-sm">
                                    <tr>
                                        <th className="p-2 border-r border-blue-300 text-center w-16">{t('tables.srNo')}</th>
                                        <th className="p-2 border-r border-blue-300">{t('attendance.employeeId')}</th>
                                        <th className="p-2 border-r border-blue-300">{t('attendance.employeeName')}</th>
                                        <th className="p-2 border-r border-blue-300">{t('attendance.department')}</th>
                                        <th className="p-2 border-r border-blue-300">{t('attendance.designation')}</th>
                                        <th className="p-2 w-56 text-center">{t('attendance.attendance')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-200">
                                    {employees.map((emp, idx) => (
                                        <tr key={emp.employeeId} className="hover:bg-blue-50/50 transition-colors">
                                            <td className="p-2 border-r border-slate-100 text-center font-bold text-gray-400">{idx + 1}</td>
                                            <td className="p-2 border-r border-slate-100 text-slate-600">{emp.employeeId}</td>
                                            <td className="p-2 border-r border-slate-100 text-slate-600">{emp.employeeName}</td>
                                            <td className="p-2 border-r border-slate-100 text-slate-600">{emp.departmentName}</td>
                                            <td className="p-2 border-r border-slate-100 text-slate-600">{emp.designationName}</td>
                                            <td className="p-2 bg-slate-50/30">
                                                <select
                                                    value={emp.statusId || ""}
                                                    onChange={(e) => handleStatusChange(emp.employeeId, e.target.value)}
                                                    className={`w-full border-2 p-1.5 rounded-lg font-bold outline-none transition-all ${emp.statusId ? 'border-blue-400 bg-white text-blue-700' : 'border-slate-300 bg-white text-gray-600'}`}
                                                >
                                                    <option value="">Select Status</option>
                                                    {ATTENDANCE_STATUSES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                                </select>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <div className="flex justify-center p-6 bg-slate-50 border-t border-slate-200">
                            <button
                                onClick={initiateSave}
                                // INDUSTRY STANDARD: Disable button if no changes detected
                                disabled={loading || modifiedIds.size === 0}
                                className={`text-sm text-white py-3 px-16 rounded-xl font-bold uppercase shadow-lg transition-all ${modifiedIds.size === 0
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-[#2c5282] hover:bg-[#1a365d] hover:-translate-y-0.5'
                                    }`}
                            >
                                {loading ? "PROCESSING..." : <span>{t('attendance.saveAttendance')}</span>}
                            </button>
                        </div>
                    </div>
                ) : hasSearched && !loading ? (
                    <div className="text-center py-20 text-slate-400 bg-slate-50 rounded-xl border-2 border-dashed">
                        {t('attendance.noEmployeeForCategory')}
                    </div>
                ) : (
                    <div className="text-center p-7 text-gray-700 border-2 border-dashed rounded-xl">
                        {t('attendance.clickOnShow')}
                        <div className="md:col-span-2">
                            <button onClick={handleShow} disabled={loading} className="bg-[#2c5282] text-sm text-white px-4 py-2 rounded mt-2 font-semibold hover:bg-[#2c5282] shadow-md transition-all active:scale-95 disabled:opacity-50">
                                {loading ? "..." : <span>{t('common.show')}</span>}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <ConfirmActionModal
                open={showConfirmModal}
                title="Confirm Attendance"
                message={`Are you sure you want to submit attendance for ${modifiedIds.size} employees on ${date}?`}
                onConfirm={handleSaveFinal}
                onClose={() => setShowConfirmModal(false)}
            />

            <MessageModal
                open={messageModal.open}
                type={messageModal.type}
                title={messageModal.title}
                message={messageModal.message}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
            />

        </PageFade>
    );
};

export default MarkAttendance;